import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusIcon, FunnelIcon, BuildingOfficeIcon, XMarkIcon, EyeIcon, CheckCircleIcon, NoSymbolIcon, ArrowDownTrayIcon } from '../../icons';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import TableColumnToggle from '../../components/layout/TableColumnToggle';
import { formatDateTime } from '../../utils/transcriptionMeta';
import { getRoleLabel } from '../../utils/adminRoles';

const COURT_TYPES = ['Supreme Court', 'Court of Appeal', 'High Court', 'Circuit Court', 'District Court'];
const GHANA_REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central', 'Volta', 'Northern',
  'Upper East', 'Upper West', 'Bono', 'Bono East', 'Ahafo', 'Western North', 'Oti', 'Savannah', 'North East',
];
const LANGUAGES = [
  { id: 'English', flag: '🇬🇧' },
  { id: 'Twi', flag: '🇬🇭' },
  { id: 'Ewe', flag: '🇬🇭' },
  { id: 'Hausa', flag: '🇬🇭' },
  { id: 'Ga', flag: '🇬🇭' },
  { id: 'French', flag: '🇫🇷' },
  { id: 'Chinese', flag: '🇨🇳' },
  // { id: 'Dagbani', flag: '🇬🇭' },
  // { id: 'Fante', flag: '🇬🇭' },
];

const COLUMN_DEFS = [
  { key: 'court', label: 'Court / Tenant' },
  { key: 'code', label: 'Code' },
  { key: 'contact', label: 'Primary Contact' },
  { key: 'region', label: 'Region' },
  { key: 'plan', label: 'Subscription Plan' },
  { key: 'status', label: 'Status' },
  { key: 'created', label: 'Created On' },
  { key: 'actions', label: 'Actions' },
];

const INITIAL_FORM = {
  court_name: '',
  court_type: '',
  region: '',
  location_detail: '',
  default_languages: ['English', 'Twi', 'Ga'],
  admin_full_name: '',
  admin_email: '',
  admin_phone: '',
  admin_role: 'Court Administrator',
};

const CreateTenantModal = ({ open, onClose, onSubmit, saving, dark }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [typeOpen, setTypeOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setTypeOpen(false);
    }
  }, [open]);

  if (!open) return null;

  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const labelCls = `text-sm font-semibold ${dark ? 'text-gray-100' : 'text-matdash-dark'}`;
  const helpCls = `text-xs mt-0.5 mb-2 ${dark ? 'text-gray-500' : 'text-matdash-muted'}`;

  const toggleLanguage = (lang) => {
    setForm((f) => ({
      ...f,
      default_languages: f.default_languages.includes(lang)
        ? f.default_languages.filter((l) => l !== lang)
        : [...f.default_languages, lang],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <form
        onSubmit={handleSubmit}
        className={`w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
              <BuildingOfficeIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>Create Court Tenant</h2>
              <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                Add a new court tenant to the platform. All settings can be updated later.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-matdash-muted hover:text-matdash-dark p-1" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>1. Court Name <span className="text-red-500">*</span></label>
              <p className={helpCls}>Enter the official name of the court.</p>
              <input
                required
                placeholder="e.g. Accra High Court"
                value={form.court_name}
                onChange={(e) => setForm({ ...form, court_name: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="relative">
              <label className={labelCls}>2. Court Type <span className="text-red-500">*</span></label>
              <p className={helpCls}>Select the type/category of the court.</p>
              <button
                type="button"
                onClick={() => setTypeOpen((v) => !v)}
                className={`${inputCls} text-left flex items-center justify-between`}
              >
                <span className={form.court_type ? '' : 'text-gray-400'}>{form.court_type || 'Select court type'}</span>
                <span className="text-matdash-muted">▾</span>
              </button>
              {typeOpen && (
                <div className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg py-1 ${dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  {COURT_TYPES.map((type) => (
                    <label key={type} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${dark ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-gray-50 text-gray-800'}`}>
                      <input
                        type="radio"
                        name="court_type"
                        checked={form.court_type === type}
                        onChange={() => { setForm({ ...form, court_type: type }); setTypeOpen(false); }}
                        className="text-matdash-primary focus:ring-matdash-primary"
                      />
                      {type}
                    </label>
                  ))}
                </div>
              )}
              <input required value={form.court_type} onChange={() => {}} className="sr-only" tabIndex={-1} />
            </div>
          </div>

          <div>
            <label className={labelCls}>3. Region / Location <span className="text-red-500">*</span></label>
            <p className={helpCls}>Select the region where the court is located.</p>
            <select
              required
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              className={`${inputCls} mb-3`}
            >
              <option value="">Select region</option>
              {GHANA_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>Detailed location (optional)</label>
            <input
              placeholder="e.g. Accra, Greater Accra Region"
              value={form.location_detail}
              onChange={(e) => setForm({ ...form, location_detail: e.target.value })}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>4. Default Enabled Transcription Languages <span className="text-red-500">*</span></label>
            <p className={helpCls}>Select the languages that will be enabled for this court by default.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {LANGUAGES.map((lang) => {
                const selected = form.default_languages.includes(lang.id);
                return (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => toggleLanguage(lang.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selected
                        ? 'border-matdash-primary bg-matdash-primary-subtle text-matdash-primary'
                        : dark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                      selected ? 'bg-matdash-primary border-matdash-primary text-white' : 'border-gray-300'
                    }`}>
                      {selected ? '✓' : ''}
                    </span>
                    <span>{lang.flag}</span>
                    {lang.id}
                  </button>
                );
              })}
            </div>
            {/* <button type="button" className="mt-2 text-sm text-matdash-primary font-medium">+ Add Other Language</button> */}
          </div>

          <div>
            <label className={labelCls}>5. Initial Court Admin <span className="text-red-500">*</span></label>
            <p className={helpCls}>Create the first administrator for this court tenant.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>Full Name</label>
                <input required placeholder="e.g. John Mensah" value={form.admin_full_name}
                  onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>Email Address</label>
                <input required type="email" placeholder="e.g. admin@accrahighcourt.gov.gh" value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>Phone Number</label>
                <div className="flex">
                  <span className={`inline-flex items-center px-2.5 rounded-l-lg border border-r-0 text-sm ${dark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>🇬🇭 +233</span>
                  <input required placeholder="e.g. 024 123 4567" value={form.admin_phone}
                    onChange={(e) => setForm({ ...form, admin_phone: e.target.value })}
                    className={`${inputCls} rounded-l-none`} />
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>Role</label>
                <select value={form.admin_role} onChange={(e) => setForm({ ...form, admin_role: e.target.value })} className={inputCls}>
                  <option>Court Administrator</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className={`flex justify-end gap-2 px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <button type="button" onClick={onClose} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
          <button type="submit" disabled={saving} className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5">
            <BuildingOfficeIcon className="h-4 w-4" />
            {saving ? 'Creating…' : 'Create Court Tenant'}
          </button>
        </div>
      </form>
    </div>
  );
};

const SuperAdminTenants = () => {
  const { dark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [viewTenant, setViewTenant] = useState(null);
  const [viewUsers, setViewUsers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    query: '',
    status: 'all',
    region: 'all',
    plan: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));
  const PER_PAGE = 10;

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await adminAPI.getTenants();
      setTenants(resp.data?.data || resp.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const [first_name, ...rest] = form.admin_full_name.trim().split(' ');
      const resp = await adminAPI.createTenant({
        name: form.court_name,
        court_name: form.court_name,
        court_type: form.court_type,
        region: form.region,
        location_detail: form.location_detail,
        default_languages: form.default_languages,
        admin_user: {
          first_name: first_name || form.admin_full_name,
          last_name: rest.join(' '),
          email: form.admin_email,
          phone: form.admin_phone,
          role: 'court_admin',
        },
      });
      const created = resp.data?.data || resp.data || {};
      const admin = created.admin_user || {};
      setShowForm(false);
      setCreatedCredentials({
        court_name: created.court_name || form.court_name,
        email: admin.email || form.admin_email,
        full_name: admin.full_name || form.admin_full_name,
        temporary_password: created.temporary_password,
      });
      toast.success('Tenant created');
      loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  const openTenantDetails = async (row) => {
    const tenantId = row.tenant_id || row.id;
    setViewTenant(row);
    setViewUsers([]);
    setViewLoading(true);
    try {
      const resp = await adminAPI.getUsers(tenantId);
      setViewUsers(resp.data?.data || resp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to load court users');
      setViewUsers([]);
    } finally {
      setViewLoading(false);
    }
  };

  const closeTenantDetails = () => {
    setViewTenant(null);
    setViewUsers([]);
    setViewLoading(false);
  };

  useEffect(() => {
    const openTenantId = location.state?.openTenantId;
    if (!openTenantId || loading || !tenants.length) return;
    const match = tenants.find(
      (t) => String(t.tenant_id || t.id) === String(openTenantId)
    );
    if (!match) return;
    openTenantDetails(match);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, loading, tenants, navigate]);

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await adminAPI.deactivateTenant(deactivateTarget.tenant_id || deactivateTarget.id);
      toast.success('Tenant deactivated');
      setDeactivateTarget(null);
      loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to deactivate tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    setSaving(true);
    try {
      await adminAPI.activateTenant(activateTarget.tenant_id || activateTarget.id);
      toast.success('Tenant activated');
      setActivateTarget(null);
      loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to activate tenant');
    } finally {
      setSaving(false);
    }
  };

  const regionOptions = useMemo(
    () => [...new Set(tenants.map((t) => t.region).filter(Boolean))],
    [tenants],
  );

  const filtered = useMemo(() => {
    return tenants
      .map((t, idx) => {
        const admin = t.admin_user || {};
        return {
          ...t,
          id: t.tenant_id || t.id,
          name: t.court_name || t.name,
          code: t.code || `${(t.region || 'GH').slice(0, 3).toUpperCase()}-${(t.court_type || 'CT').slice(0, 2).toUpperCase()}-${idx + 1}`,
          primary_contact: t.primary_contact || admin.full_name || '—',
          contact_email: t.contact_email || admin.email || '—',
          contact_phone: t.contact_phone || admin.phone || '—',
          subscription_plan: t.subscription_plan || 'Standard',
        };
      })
      .filter((t) => {
        const q = filters.query.trim().toLowerCase();
        const matchesQuery = !q
          || t.name?.toLowerCase().includes(q)
          || t.code?.toLowerCase().includes(q)
          || t.region?.toLowerCase().includes(q)
          || t.contact_email?.toLowerCase().includes(q)
          || t.primary_contact?.toLowerCase().includes(q);
        const matchesStatus = filters.status === 'all' || t.status === filters.status;
        const matchesRegion = filters.region === 'all' || t.region === filters.region;
        const matchesPlan = filters.plan === 'all' || t.subscription_plan === filters.plan;
        const matchesDate = (() => {
          if (!filters.dateFrom && !filters.dateTo) return true;
          const created = t.created_at ? new Date(t.created_at).getTime() : NaN;
          if (Number.isNaN(created)) return true;
          if (filters.dateFrom) {
            const from = new Date(`${filters.dateFrom}T00:00:00`).getTime();
            if (created < from) return false;
          }
          if (filters.dateTo) {
            const to = new Date(`${filters.dateTo}T23:59:59`).getTime();
            if (created > to) return false;
          }
          return true;
        })();
        return matchesQuery && matchesStatus && matchesRegion && matchesPlan && matchesDate;
      });
  }, [tenants, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const show = (key) => visibleKeys.includes(key);
  const colCount = visibleKeys.length || 1;

  const inputCls = `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dateCls = `px-3 h-9 py-1.5 text-sm rounded-lg border shrink-0 w-[9.75rem] focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`;

  const exportCsv = () => {
    const headers = ['Court', 'Code', 'Contact', 'Email', 'Phone', 'Region', 'Plan', 'Status', 'Created'];
    const rows = filtered.map((r) => [r.name, r.code, r.primary_contact, r.contact_email, r.contact_phone, r.region, r.subscription_plan, r.status, r.created_at]);
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-tenants.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-tenants.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  return (
    <div>
      <div className={`md-card ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`relative z-30 px-4 py-3 border-b flex flex-nowrap items-center gap-2 overflow-x-auto ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative flex-1 min-w-[180px] max-w-sm shrink">
            <FunnelIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={filters.query}
              onChange={(e) => { setFilters((f) => ({ ...f, query: e.target.value })); setPage(1); }}
              placeholder="Search courts..."
              className={`${inputCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => { setFilters((f) => ({ ...f, dateFrom: e.target.value })); setPage(1); }}
            className={dateCls}
            aria-label="From date"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => { setFilters((f) => ({ ...f, dateTo: e.target.value })); setPage(1); }}
            className={dateCls}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
              </button>
              <div className={`absolute right-0 top-full pt-1 z-[80] hidden group-hover:block`}>
                <div className={`w-64 p-3 rounded-xl border shadow-lg space-y-3 ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                    <select value={filters.status} onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }} className={inputCls}>
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Region</label>
                    <select value={filters.region} onChange={(e) => { setFilters((f) => ({ ...f, region: e.target.value })); setPage(1); }} className={inputCls}>
                      <option value="all">All Regions</option>
                      {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Subscription Plan</label>
                    <select value={filters.plan} onChange={(e) => { setFilters((f) => ({ ...f, plan: e.target.value })); setPage(1); }} className={inputCls}>
                      <option value="all">All Plans</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="Professional">Professional</option>
                      <option value="Standard">Standard</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setFilters({ query: '', status: 'all', region: 'all', plan: 'all', dateFrom: '', dateTo: '' }); setPage(1); }}
                    className="text-xs text-matdash-primary"
                  >
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <TableColumnToggle
              columns={COLUMN_DEFS}
              visibleKeys={visibleKeys}
              onToggle={(key) => setVisibleKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]))}
              label=""
            />

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className="absolute right-0 top-full pt-1 z-[80] hidden group-hover:block">
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-matdash-primary text-white text-sm hover:bg-[#a04e08]"
            >
              <PlusIcon className="h-4 w-4" />
              Create Tenant
            </button>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          Total Courts: {filtered.length} · Showing {filtered.length ? (page - 1) * PER_PAGE + 1 : 0} to {Math.min(page * PER_PAGE, filtered.length)}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                {show('court') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Court / Tenant</th>}
                {show('code') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Code</th>}
                {show('contact') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Primary Contact</th>}
                {show('region') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Region</th>}
                {show('plan') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Subscription Plan</th>}
                {show('status') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Status</th>}
                {show('created') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Created On</th>}
                {show('actions') && <th className="px-4 py-3 text-left text-xs font-semibold text-matdash-muted uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className={`divide-y ${dark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading && <tr><td className="px-4 py-6 text-center text-sm text-matdash-muted" colSpan={colCount}>Loading…</td></tr>}
              {!loading && pageRows.length === 0 && <tr><td className="px-4 py-6 text-center text-sm text-matdash-muted" colSpan={colCount}>No matching courts found.</td></tr>}
              {!loading && pageRows.map((row) => (
                <tr key={row.id} className={dark ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'}>
                  {show('court') && (
                    <td className="px-4 py-3">
                      <p className={`text-sm font-semibold ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{row.name}</p>
                      <p className="text-xs text-matdash-muted">{row.region} Region</p>
                    </td>
                  )}
                  {show('code') && <td className="px-4 py-3 text-sm text-matdash-muted">{row.code}</td>}
                  {show('contact') && (
                    <td className="px-4 py-3">
                      <p className={`text-sm font-medium ${dark ? 'text-gray-200' : 'text-matdash-dark'}`}>{row.primary_contact}</p>
                      <p className="text-xs text-matdash-muted">{row.contact_email}</p>
                      <p className="text-xs text-matdash-muted">{row.contact_phone}</p>
                    </td>
                  )}
                  {show('region') && <td className="px-4 py-3 text-sm text-matdash-muted">{row.region}</td>}
                  {show('plan') && (
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        row.subscription_plan === 'Enterprise' ? 'bg-blue-50 text-blue-700'
                          : row.subscription_plan === 'Professional' ? 'bg-purple-50 text-purple-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}>{row.subscription_plan}</span>
                    </td>
                  )}
                  {show('status') && (
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                          : row.status === 'maintenance' ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {row.status === 'active' ? 'Active' : row.status === 'maintenance' ? 'Maintenance' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  {show('created') && <td className="px-4 py-3 text-xs text-matdash-muted">{row.created_at ? formatDateTime(row.created_at) : '—'}</td>}
                  {show('actions') && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          title="View"
                          aria-label="View tenant"
                          onClick={() => openTenantDetails(row)}
                          className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                            dark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-matdash-primary hover:bg-matdash-primary-subtle'
                          }`}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {row.status === 'active' || row.status === 'maintenance' ? (
                          <button
                            type="button"
                            title="Deactivate"
                            aria-label="Deactivate tenant"
                            onClick={() => setDeactivateTarget(row)}
                            className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                              dark ? 'border-gray-700 text-red-400 hover:bg-gray-700' : 'border-gray-200 text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <NoSymbolIcon className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Activate"
                            aria-label="Activate tenant"
                            onClick={() => setActivateTarget(row)}
                            className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                              dark ? 'border-gray-700 text-emerald-400 hover:bg-gray-700' : 'border-gray-200 text-emerald-600 hover:bg-emerald-50'
                            }`}
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex items-center justify-end gap-1 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-7 h-7 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" onClick={() => setPage(n)} className={`w-7 h-7 text-xs rounded ${page === n ? 'bg-matdash-primary text-white' : 'border border-matdash-border text-matdash-muted'}`}>{n}</button>
          ))}
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-7 h-7 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
        </div>
      </div>

      <CreateTenantModal open={showForm} onClose={() => setShowForm(false)} onSubmit={handleCreate} saving={saving} dark={dark} />

      {viewTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeTenantDetails}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-tenant-title"
            className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border shadow-lg ${
              dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-matdash-primary flex items-center justify-center flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 id="view-tenant-title" className={`text-lg font-semibold mb-0 truncate ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>
                    {viewTenant.court_name || viewTenant.name}
                  </h2>
                  <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                    Court details and users
                  </p>
                </div>
              </div>
              <button type="button" onClick={closeTenantDetails} className="text-matdash-muted p-1" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <dl className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm border-b border-matdash-border">
              <div>
                <dt className="text-matdash-muted">Court type</dt>
                <dd className={`mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{viewTenant.court_type || '—'}</dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Region</dt>
                <dd className={`mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{viewTenant.region || '—'}</dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Status</dt>
                <dd className="mt-0.5">
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                    viewTenant.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                      : viewTenant.status === 'maintenance' ? 'bg-amber-50 text-amber-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {viewTenant.status === 'active' ? 'Active' : viewTenant.status === 'maintenance' ? 'Maintenance' : 'Inactive'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Created</dt>
                <dd className={`mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                  {viewTenant.created_at ? formatDateTime(viewTenant.created_at) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Primary contact</dt>
                <dd className={`mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{viewTenant.primary_contact || '—'}</dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Contact email</dt>
                <dd className={`mt-0.5 break-all ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{viewTenant.contact_email || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-matdash-muted">Languages</dt>
                <dd className={`mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                  {(viewTenant.default_languages || []).length
                    ? (viewTenant.default_languages || []).join(', ')
                    : '—'}
                </dd>
              </div>
            </dl>

            <div className="px-6 py-5">
              <h3 className={`text-sm font-semibold mb-3 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                Users ({viewLoading ? '…' : viewUsers.length})
              </h3>
              {viewLoading ? (
                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>Loading users…</p>
              ) : viewUsers.length === 0 ? (
                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>No users found for this court.</p>
              ) : (
                <div className={`overflow-x-auto rounded-lg border ${dark ? 'border-gray-800' : 'border-gray-200'}`}>
                  <table className="min-w-full text-sm">
                    <thead className={dark ? 'bg-gray-800' : 'bg-gray-50'}>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-matdash-muted uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-matdash-muted uppercase">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-matdash-muted uppercase">Role</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-matdash-muted uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${dark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                      {viewUsers.map((u) => (
                        <tr key={u.user_id || u.id}>
                          <td className={`px-3 py-2 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                            {u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                          </td>
                          <td className={`px-3 py-2 break-all ${dark ? 'text-gray-300' : 'text-matdash-muted'}`}>
                            {u.email || '—'}
                          </td>
                          <td className={`px-3 py-2 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                            {getRoleLabel(u.role)}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              u.is_active || u.status === 'active'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {u.is_active || u.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {createdCredentials && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={`rounded-xl shadow-xl max-w-md w-full p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
            <h2 className={`text-base font-semibold ${dark ? 'text-gray-50' : 'text-gray-900'}`}>
              Court Admin credentials
            </h2>
            <p className={`text-sm mt-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              Save this temporary password now — it is shown only once.
            </p>
            <div className={`mt-4 rounded-lg border p-3 space-y-2 text-sm ${dark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <p><span className="text-matdash-muted">Court:</span> {createdCredentials.court_name}</p>
              <p><span className="text-matdash-muted">Admin:</span> {createdCredentials.full_name}</p>
              <p><span className="text-matdash-muted">Email:</span> {createdCredentials.email}</p>
              <p>
                <span className="text-matdash-muted">Temporary password:</span>{' '}
                <code className={`font-mono font-semibold ${dark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  {createdCredentials.temporary_password || '—'}
                </code>
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="md-btn md-btn-primary md-btn-sm"
                onClick={() => {
                  if (createdCredentials.temporary_password) {
                    navigator.clipboard?.writeText(createdCredentials.temporary_password);
                    toast.success('Password copied');
                  }
                  setCreatedCredentials(null);
                }}
              >
                Copy password & close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate tenant"
        message={`Deactivate ${deactivateTarget?.court_name || deactivateTarget?.name}? All users will lose access immediately.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        danger
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(activateTarget)}
        title="Activate tenant"
        message={`Activate ${activateTarget?.court_name || activateTarget?.name}? Users will regain access.`}
        confirmLabel="Activate"
        onConfirm={handleActivate}
        onCancel={() => setActivateTarget(null)}
        loading={saving}
      />
    </div>
  );
};

export default SuperAdminTenants;
